import {
  IRuleGroupDeleteRequest,
  IRuleGroupDeleteResponse,
  IRuleGroupUpdateRequest,
  IRuleGroupUpdateResponse,
} from "types/AdjustmentRules/AdjustmentRules";
import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IGFEOrderPricingCalculationRequest,
  IGFEOrderPricingRequest,
  IMatchingOrdersRequest,
  IMatchingOrdersResponse,
} from "types/Order/Order";
import {
  IGetPrecalculatedAllowablesParams,
  IPrecalculatedAllowablesResponse,
} from "types/precalculatedAllowables.types";
import {
  ICaseRateRuleset,
  ICaseRateRulesetCreateRequest,
  ICaseRateRulesetGroupData,
  ICaseRateRulesetUpdateRequest,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRuleset";
import {
  IAllCaseRateRulesetResponse,
  ICaseRateRulesetResponse,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRulesetResponse";

import { api } from ".";

const accountQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    pricing: builder.mutation<any, IGFEOrderPricingRequest>({
      query: (pricing) => ({
        url: "pricing",
        method: "POST",
        body: pricing,
      }),
    }),
    getEstimateCalculation: builder.query<
      any,
      IGFEOrderPricingCalculationRequest
    >({
      query: (pricing) => ({
        url: "pricing/calculateEstimate",
        method: "POST",
        body: pricing,
      }),
    }),
    // CLE-2769 follow-up — the matching-orders lookup on its own, so the deductible-reduction
    // banner can show as soon as the patient and visit date are typed, before any CPT.
    getMatchingOrders: builder.query<
      IMatchingOrdersResponse,
      IMatchingOrdersRequest
    >({
      query: (params) => {
        const search = new URLSearchParams();
        search.set("firstName", params.firstName);
        search.set("lastName", params.lastName);
        search.set("dateOfBirth", params.dateOfBirth);
        search.set("dateOfService", params.dateOfService);
        if (params.excludeOrderId) {
          search.set("excludeOrderId", params.excludeOrderId);
        }
        return `pricing/matchingOrders?${search.toString()}`;
      },
    }),
    getCaseRateRulesets: builder.query<
      IAllCaseRateRulesetResponse,
      | (IRulesetListParams & {
          providerId?: string;
          payerId?: number;
        })
      | null
      | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.applicability?.length) {
          search.set("applicability", params.applicability.join(","));
        } else if (params?.isDefault !== undefined) {
          search.set("isDefault", String(params.isDefault));
        }
        if (params?.providerId) {
          search.set("providerId", params.providerId);
        }
        if (params?.payerId !== undefined && params?.payerId !== null) {
          search.set("payerId", String(params.payerId));
        }
        const qs = search.toString();
        return `pricing/caseRateRulesets${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["CaseRateRulesets"],
    }),
    getCaseRateRulesetById: builder.query<
      ICaseRateRulesetResponse,
      string | undefined
    >({
      query: (id: string) => `pricing/caseRateRulesets/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "CaseRateRulesets", id: id ?? "LIST" },
      ],
    }),
    createCaseRateRuleset: builder.mutation<
      ICaseRateRuleset,
      ICaseRateRulesetCreateRequest
    >({
      query: (newRuleset) => ({
        url: "pricing/caseRateRuleset",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["CaseRateRulesets", "ContractFeeSchedules"],
    }),
    updateCaseRateRuleset: builder.mutation<
      ICaseRateRuleset,
      ICaseRateRulesetUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `pricing/caseRateRuleset/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    // CLE-2842 — edits every row of a bucket (same rule, same provider, different payers) in
    // one server transaction; never emulate it with a fan-out of single-row PUTs.
    updateCaseRateRulesetGroup: builder.mutation<
      IRuleGroupUpdateResponse<ICaseRateRuleset>,
      IRuleGroupUpdateRequest<ICaseRateRulesetGroupData>
    >({
      query: (body) => ({
        url: "pricing/caseRateRuleset/group",
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    // CLE-2842 — deletes every row of a bucket in one server transaction (409 when a listed row
    // is missing or drifted, with nothing deleted); never emulate it with single DELETEs.
    deleteCaseRateRulesetGroup: builder.mutation<
      IRuleGroupDeleteResponse,
      IRuleGroupDeleteRequest
    >({
      query: (body) => ({
        url: "pricing/caseRateRuleset/group/delete",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    deleteCaseRateRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `pricing/caseRateRuleset/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    cloneCaseRateAsDefault: builder.mutation<ICaseRateRulesetResponse, string>({
      query: (id) => ({
        url: `pricing/caseRateRuleset/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["CaseRateRulesets"],
    }),
    copyCaseRateToContract: builder.mutation<
      ICaseRateRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `pricing/caseRateRuleset/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CaseRateRulesets", "ContractFeeSchedules"],
    }),
    getCaseRatePropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `pricing/caseRateRuleset/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `caseRate:${id}` },
      ],
    }),
    applyCaseRatePropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `pricing/caseRateRuleset/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "CaseRateRulesets",
        "ContractFeeSchedules",
        { type: "DefaultRulePropagation", id: `caseRate:${id}` },
      ],
    }),
    getPrecalculatedAllowables: builder.query<
      IPrecalculatedAllowablesResponse,
      IGetPrecalculatedAllowablesParams
    >({
      query: ({ accountId, payerId, caseSignature, dateOfService }) => {
        const params = new URLSearchParams({
          accountId,
          payerId: payerId.toString(),
          caseSignature,
        });
        // CLE-2299: scope the lookup to the claim's service date so we don't return a
        // stale canonical (NULL-serviceDate) row that disagrees with the claim's own
        // expectedAmount after a config-driven repricing.
        if (dateOfService) params.append("dateOfService", dateOfService);
        return {
          url: `pricing/precalculated-allowables?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PrecalculatedAllowables"],
    }),
  }),
});

export const {
  usePricingMutation,
  useGetEstimateCalculationQuery,
  useLazyGetEstimateCalculationQuery,
  useGetMatchingOrdersQuery,
  useGetCaseRateRulesetsQuery,
  useCreateCaseRateRulesetMutation,
  useDeleteCaseRateRulesetMutation,
  useDeleteCaseRateRulesetGroupMutation,
  useUpdateCaseRateRulesetMutation,
  useUpdateCaseRateRulesetGroupMutation,
  useGetCaseRateRulesetByIdQuery,
  useCloneCaseRateAsDefaultMutation,
  useCopyCaseRateToContractMutation,
  useGetCaseRatePropagationPreviewQuery,
  useLazyGetCaseRatePropagationPreviewQuery,
  useApplyCaseRatePropagationMutation,
  useGetPrecalculatedAllowablesQuery,
} = accountQueries;
